import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Grid,
  Menu,
  MenuItem,
  InputAdornment,
  Avatar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Paper
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { superadminService } from '../../../services/superadmin.service';
import { theme } from '../../../theme';
import ClientCard from '../components/ClientCard';

const ClientsList = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [viewMode, setViewMode] = useState('card');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');

  // Action state
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Add Client State
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addClientLoading, setAddClientLoading] = useState(false);
  const [addClientError, setAddClientError] = useState('');
  const [newClient, setNewClient] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  const handleOpenAddClient = () => {
    setNewClient({ first_name: '', last_name: '', email: '', phone: '' });
    setAddClientError('');
    setAddClientOpen(true);
  };

  const handleCloseAddClient = () => {
    setAddClientOpen(false);
  };

  const handleSubmitAddClient = async (e) => {
    e.preventDefault();
    if (!newClient.first_name || !newClient.last_name || !newClient.email || !newClient.phone) {
      setAddClientError('Veuillez remplir les champs obligatoires.');
      return;
    }

    try {
      setAddClientLoading(true);
      setAddClientError('');
      await superadminService.createClient(newClient);
      showToast('Client ajouté avec succès.', 'success');
      setAddClientOpen(false);
      load(); // Reload list
    } catch (err) {
      console.error(err);
      setAddClientError(err.response?.data?.detail || 'Erreur lors de la création du client.');
    } finally {
      setAddClientLoading(false);
    }
  };

  // Toast State
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const load = async (termOverride) => {
    try {
      setError('');
      setLoading(true);
      const term = typeof termOverride === 'string' ? termOverride : submittedSearch;
      const res = await superadminService.listClients({
        search: term,
        page: page + 1,
        page_size: pageSize
      });
      const data = res?.data;
      if (data?.results) {
        setItems(data.results);
        setTotal(data.count || 0);
      } else {
        setItems(data || []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [submittedSearch, page, pageSize]);

  const handleActionMenuOpen = (event, item) => {
    setSelectedItem(item);
    setActionAnchorEl(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
    setSelectedItem(null);
  };

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [loadingReset, setLoadingReset] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [clientToDelete, setClientToDelete] = useState(null);

  // Edit Dialog State
  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    is_active: true
  });
  const [editingClient, setEditingClient] = useState(null);

  const handleOpenEdit = (client) => {
    setEditingClient(client);
    setEditForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      is_active: client.is_active ?? true
    });
    setEditError('');
    setEditOpen(true);
    handleActionMenuClose();
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditingClient(null);
  };

  const handleSubmitEdit = async () => {
    if (!editingClient) return;
    try {
      setEditLoading(true);
      setEditError('');
      await superadminService.updateClient(editingClient.id, editForm);
      showToast('Client mis à jour avec succès.', 'success');
      setEditOpen(false);
      await load(submittedSearch);
    } catch (e) {
      console.error(e);
      setEditError(e?.response?.data?.detail || 'Erreur lors de la mise à jour.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRequestResetPassword = (userId) => {
    setUserToReset(userId);
    setConfirmOpen(true);
    handleActionMenuClose();
  };

  const handleConfirmResetPassword = async () => {
    if (!userToReset) return;
    try {
      setLoadingReset(true);
      await superadminService.resetUserPassword(userToReset);
      showToast('Email de réinitialisation envoyé.', 'success');
      setConfirmOpen(false);
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de l\'envoi.', 'error');
    } finally {
      setLoadingReset(false);
      setUserToReset(null);
    }
  };

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      setViewMode(nextView);
    }
  };

  const handleOpenDelete = (client) => {
    setDeleteError('');
    setClientToDelete(client);
    setDeleteOpen(true);
    handleActionMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!clientToDelete?.id) return;
    try {
      setDeleteLoading(true);
      setDeleteError('');
      await superadminService.deleteClient(clientToDelete.id);
      showToast('Client supprimé.', 'success');
      setDeleteOpen(false);
      setClientToDelete(null);
      await load(submittedSearch);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || 'Erreur lors de la suppression du client.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const rows = useMemo(() => items || [], [items]);

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 12 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 4 }}>
        <Box sx={{ mb: { xs: 0.5, md: 0 } }}>
          <Typography variant="h4" sx={{ fontWeight: 900, color: theme.colors.text.primary, mb: 0.25, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
            Clients
          </Typography>
          <Typography variant="body1" sx={{ color: theme.colors.text.secondary, fontSize: { xs: '0.75rem', sm: '1rem' } }}>
            Gérez les comptes clients
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon fontSize="small" />}
            onClick={handleOpenAddClient}
            size="small"
            sx={{
              fontWeight: 700,
              textTransform: 'none',
              px: { xs: 1.5, sm: 3 },
              flex: { xs: 1, md: 'none' },
              height: { xs: 36, sm: 'auto' },
              backgroundColor: theme.colors.primary[500],
              '&:hover': { backgroundColor: theme.colors.primary[600] }
            }}
          >
            Ajouter <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>un client</Box>
          </Button>
          {/* View Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="view mode"
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.5)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: theme.borderRadius.lg,
              flex: { xs: 1, md: 'none' },
              height: { xs: 36, sm: 'auto' },
              justifyContent: 'center',
              '& .MuiToggleButton-root': {
                border: 'none',
                px: { xs: 1, sm: 2 },
                py: 1,
                flex: 1
              }
            }}
          >
            <ToggleButton value="card">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Search Bar */}
      <Card
        sx={{
          borderRadius: theme.borderRadius.xl,
          border: 'none',
          boxShadow: theme.boxShadow.sm,
          backgroundColor: '#fff',
          mb: 4,
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              setSubmittedSearch(search.trim());
            }}
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
          >
            <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
              <SearchIcon sx={{ color: theme.colors.text.tertiary, fontSize: 22, ml: 2, mr: 1.5 }} />
              <TextField
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, email, téléphone..."
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  style: { fontSize: '0.95rem', fontWeight: 500 }
                }}
              />
              {search && (
                <IconButton size="small" onClick={() => { setSearch(''); setSubmittedSearch(''); load(''); }} sx={{ mr: 1 }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: theme.borderRadius.lg,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: theme.colors.text.primary,
                color: '#fff',
                px: { xs: 1.5, sm: 3 },
                my: 0.5,
                mr: 0.5,
                '&:hover': { backgroundColor: '#000' }
              }}
            >
              Rechercher
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 4, textAlign: 'center', bgcolor: theme.colors.error[50], borderRadius: theme.borderRadius.xl, color: theme.colors.error[700] }}>
          <Typography fontWeight={700}>{error}</Typography>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 8, textAlign: 'center', bgcolor: theme.colors.background.secondary, borderRadius: theme.borderRadius.xl, border: `1px dashed ${theme.colors.border.light}` }}>
          <Typography variant="h6" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>Aucun client trouvé</Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.tertiary }}>Essayez de modifier votre recherche.</Typography>
        </Box>
      ) : (
        <>
          {viewMode === 'list' ? (
            /* LIST VIEW */
            <Fade in>
              <Card sx={{ borderRadius: theme.borderRadius.xl, border: `1px solid ${theme.colors.border.light}`, boxShadow: theme.boxShadow.sm, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table sx={{ minWidth: { xs: '100%', md: 700 } }}>
                    <TableHead sx={{ bgcolor: theme.colors.background.secondary }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, color: theme.colors.text.secondary, py: 2 }}>Client</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: theme.colors.text.secondary, py: 2, display: { xs: 'none', md: 'table-cell' } }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: theme.colors.text.secondary, py: 2, display: { xs: 'none', md: 'table-cell' } }}>Téléphone</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: theme.colors.text.secondary, py: 2 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((client) => {
                        const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Nom inconnu';
                        const initial = fullName.charAt(0).toUpperCase();

                        return (
                          <TableRow key={client.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                  sx={{
                                    display: { xs: 'none', md: 'flex' },
                                    bgcolor: theme.colors.primary[100],
                                    color: theme.colors.primary[700],
                                    fontWeight: 800,
                                    width: 40,
                                    height: 40
                                  }}
                                >
                                  {initial}
                                </Avatar>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>
                                  {fullName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ color: theme.colors.text.secondary, display: { xs: 'none', md: 'table-cell' } }}>{client.email}</TableCell>
                            <TableCell sx={{ color: theme.colors.text.secondary, display: { xs: 'none', md: 'table-cell' } }}>{client.phone || '—'}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={(e) => handleActionMenuOpen(e, client)}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Card>
            </Fade>
          ) : (
            /* CARD VIEW */
            <Fade in>
              <Grid container spacing={3}>
                {rows.map((client) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={client.id}>
                    <ClientCard
                      client={client}
                      onResetPassword={() => handleRequestResetPassword(client.id)}
                      onEdit={() => handleOpenEdit(client)}
                      onDelete={() => handleOpenDelete(client)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Fade>
          )}

          {total > 0 && (
            <Box sx={{
              position: 'fixed',
              bottom: 0,
              right: 0,
              left: { xs: 0, md: '280px' },
              zIndex: 1000,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}>
              <Paper
                elevation={3}
                sx={{
                  pointerEvents: 'auto',
                  borderRadius: theme.borderRadius.xl,
                  border: `1px solid ${theme.colors.border.light}`,
                  overflow: 'hidden',
                  width: 'fit-content'
                }}
              >
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage="Lignes par page"
                  sx={{ bgcolor: 'white' }}
                />
              </Paper>
            </Box>
          )}
        </>
      )}

      {/* Global Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 1.5,
            borderRadius: theme.borderRadius.lg,
            border: `1px solid ${theme.colors.border.light}`,
            '& .MuiMenuItem-root': {
              fontSize: '0.9rem',
              fontWeight: 600,
              color: theme.colors.text.secondary,
              py: 1,
              px: 2,
              gap: 1.5,
              '&:hover': {
                backgroundColor: theme.colors.background.secondary,
                color: theme.colors.primary[600],
              }
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => selectedItem && handleOpenEdit(selectedItem)}>
          <EditIcon fontSize="small" /> Modifier
        </MenuItem>
        <MenuItem onClick={() => selectedItem && handleRequestResetPassword(selectedItem.id)}>
          <LockResetIcon fontSize="small" /> Réinitialiser mot de passe
        </MenuItem>
        <MenuItem onClick={() => selectedItem && handleOpenDelete(selectedItem)} sx={{ color: theme.colors.error[600] }}>
          <DeleteIcon fontSize="small" /> Supprimer
        </MenuItem>
      </Menu>

      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Modifier le client</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editError && <Alert severity="error">{editError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Prénom"
                  fullWidth
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Nom"
                  fullWidth
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </Grid>
            </Grid>
            <TextField
              label="Email"
              fullWidth
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
            <TextField
              label="Téléphone"
              fullWidth
              value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Statut du compte</Typography>
              <ToggleButtonGroup
                value={editForm.is_active}
                exclusive
                onChange={(_, v) => { if (v !== null) setEditForm({ ...editForm, is_active: v }); }}
                size="small"
              >
                <ToggleButton value={true} color="success">Actif</ToggleButton>
                <ToggleButton value={false} color="error">Inactif</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEdit}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmitEdit} disabled={editLoading}>
            {editLoading ? <CircularProgress size={24} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={addClientOpen} onClose={handleCloseAddClient} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmitAddClient}>
          <DialogTitle sx={{ fontWeight: 800 }}>Ajouter un nouveau client</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {addClientError && <Alert severity="error">{addClientError}</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    required
                    label="Prénom"
                    fullWidth
                    value={newClient.first_name}
                    onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    required
                    label="Nom"
                    fullWidth
                    value={newClient.last_name}
                    onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                  />
                </Grid>
              </Grid>
              <TextField
                required
                label="Email"
                type="email"
                fullWidth
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                helperText="Le client recevra ses identifiants à cette adresse."
              />
              <TextField
                required
                label="Téléphone"
                fullWidth
                value={newClient.phone}
                onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseAddClient}>Annuler</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={addClientLoading}
              sx={{
                backgroundColor: theme.colors.primary[500],
                '&:hover': { backgroundColor: theme.colors.primary[600] }
              }}
            >
              {addClientLoading ? <CircularProgress size={24} color="inherit" /> : 'Ajouter'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmer la réinitialisation</DialogTitle>
        <DialogContent>
          <Typography>
            Voulez-vous vraiment envoyer un email de réinitialisation de mot de passe à cet utilisateur ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleConfirmResetPassword} disabled={loadingReset}>
            {loadingReset ? <CircularProgress size={24} color="inherit" /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => { if (!deleteLoading) { setDeleteOpen(false); setClientToDelete(null); } }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Supprimer le client</DialogTitle>
        <DialogContent>
          <Typography>
            Confirmez-vous la suppression de <b>{clientToDelete?.first_name} {clientToDelete?.last_name}</b> ?
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setDeleteOpen(false); setClientToDelete(null); }} disabled={deleteLoading}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={24} color="inherit" /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontWeight: 600, borderRadius: theme.borderRadius.md }} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientsList;
